/**
 * LayerConfig - Shared mesh layer configuration for terrain rendering
 *
 * Defines the three terrain layers (solid, transparent, liquid) and provides
 * a factory function to create Three.js meshes with the correct material,
 * shadow, and render-order settings. Used by both ChunkMesh (standalone
 * per-chunk meshes) and TerrainBatch (merged group meshes).
 */

import * as THREE from 'three';
import {
  getTerrainMaterial,
  getTransparentTerrainMaterial,
  getLiquidTerrainMaterial,
  getTransparentDepthMaterial,
} from './VoxelMaterials.js';
import { applyLodTag, type LodTag } from '../material/LodDebugMaterials.js';

// ============== Layer Indices ==============

export const LAYER_SOLID = 0;
export const LAYER_TRANSPARENT = 1;
export const LAYER_LIQUID = 2;
export const LAYER_COUNT = 3;

// ============== Layer Configuration ==============

interface MeshLayerConfig {
  material: () => THREE.Material;
  castShadow: boolean;
  receiveShadow: boolean;
  renderOrder: number;
  customDepthMaterial?: () => THREE.Material;
  meshType: string;
}

const LAYER_CONFIGS: readonly MeshLayerConfig[] = [
  { // SOLID
    material: getTerrainMaterial,
    castShadow: true,
    receiveShadow: true,
    renderOrder: 0,
    meshType: 'solid',
  },
  { // TRANSPARENT
    material: getTransparentTerrainMaterial,
    castShadow: true,
    receiveShadow: true,
    renderOrder: 1,
    customDepthMaterial: getTransparentDepthMaterial,
    meshType: 'transparent',
  },
  { // LIQUID
    material: getLiquidTerrainMaterial,
    castShadow: false,
    receiveShadow: true,
    renderOrder: 2,
    meshType: 'liquid',
  },
];

// ============== Vertex Attribute Layout ==============

// The layout and byte format live in vertexFormat.ts (importable from the mesh worker, which this
// module — it builds materials — is not). Re-exported so the TerrainBatch and the seam stitcher keep
// one import.
export { TERRAIN_ATTRS, SKIRT_KIND_ATTR, MATERIAL_SLOT_STRIDE, allocAttrArray, bufferAttribute, unorm8 } from './vertexFormat.js';
export type { AttrDef, AttrArray } from './vertexFormat.js';

// ============== Factory ==============

/**
 * Create a Three.js mesh with the correct material, shadow, and render-order
 * settings for the given terrain layer.
 *
 * `lodTag` is what the F7 LOD view colours the mesh by — its LOD level, or 'skirt' for seam
 * ribbons. Pass the real one: an untagged mesh is drawn as lod 0 and lies about the LOD map.
 */
export function createLayerMesh(
  geometry: THREE.BufferGeometry,
  layer: number,
  chunkKey?: string,
  lodTag: LodTag = 0,
): THREE.Mesh {
  const config = LAYER_CONFIGS[layer];
  const mesh = new THREE.Mesh(geometry, config.material());
  configureLayerMesh(mesh, layer, lodTag);
  if (chunkKey) {
    mesh.userData.chunkKey = chunkKey;
    mesh.userData.meshType = config.meshType;
  }
  return mesh;
}

/** The layer's shared material — what a BatchedMesh for the layer is constructed with. */
export function layerMaterial(layer: number): THREE.Material {
  return LAYER_CONFIGS[layer].material();
}

/**
 * Give any mesh (a per-chunk mesh, a merged group, a TerrainBatch) the layer's settings: LOD tag,
 * shadows, render order, and the transparent layer's alpha-tested depth material.
 */
export function configureLayerMesh(mesh: THREE.Mesh, layer: number, lodTag: LodTag = 0): void {
  const config = LAYER_CONFIGS[layer];
  applyLodTag(mesh, lodTag);
  mesh.castShadow = config.castShadow;
  mesh.receiveShadow = config.receiveShadow;
  mesh.renderOrder = config.renderOrder;
  if (config.customDepthMaterial) {
    mesh.customDepthMaterial = config.customDepthMaterial();
  }
}
